import type { CalculatorArticleContent } from "../calculator-article";

export const oneRmGuide: CalculatorArticleContent = {
  id: "one-rm",
  eyebrow: "Güç ve yük planlama rehberi",
  updatedAt: "29 Eylül 2026",
  articleTitle: "1RM tahminini antrenmanda doğru kullanmak",
  intro:
    "1RM, bir harekette teknik standardı koruyarak tek tekrarda kaldırabildiğin en yüksek yükü ifade eder. Bu hesaplayıcı ise tamamladığın bir setten tahmini 1RM (E1RM) üretir. Sonuç, o gün platformda kesin olarak kaldıracağın sayı değil; yük seçimini, ilerlemeyi ve program yüzdelerini yönetmek için kullanabileceğin bir tahmindir.",
  sections: [
    {
      id: "bir-rm-ve-e1rm",
      title: "1RM ile E1RM arasındaki fark",
      paragraphs: [
        "Gerçek 1RM, belirli bir hareket standardıyla tamamlanmış maksimum tek tekrardır. E1RM ise daha hafif bir yükle yapılan çok tekrarlı seti matematiksel bir denklemle tek tekrar performansına çevirir. Aynı görünseler de ölçtükleri şey aynı değildir: biri doğrudan performans, diğeri model tahminidir.",
        "E1RM özellikle sık maksimum deneme yapmak istemediğinde kullanışlıdır. Ağır fakat kontrol edilebilir bir çalışma setini girerek güç eğilimini izleyebilir, sonraki haftanın yüklerini belirleyebilir ve program yüzdelerini güncelleyebilirsin. Bunun karşılığında sonuç; tekrar rezervi, teknik, tempo, ekipman ve yorgunluk gibi etkenlere duyarlıdır.",
        "Örneğin 100 kg ile beş tekrar yapan iki sporcunun E1RM sonucu aynı çıkabilir. Ancak biri beşinci tekrardan sonra iki tekrar daha yapabilecek durumdayken diğeri gerçek tükenişe ulaşmış olabilir. Denklem bu farkı görmez. Bu nedenle hesaplanan değeri setin RPE veya RIR bilgisiyle birlikte okumak gerekir.",
      ],
      subsections: [
        {
          title: "Doğrudan maksimum test ne zaman anlamlıdır?",
          paragraphs: [
            "Yarışma hazırlığının uygun bir döneminde, teknik standardın oturduğunda ve sağlık açısından engelin olmadığında doğrudan test yapılabilir. Her hafta 1RM denemek ise gelişimi ölçmekten çok yorgunluk biriktirebilir. Çoğu antrenman döneminde benzer koşullarda alınmış E1RM eğilimi daha sürdürülebilir bir geri bildirim sağlar.",
          ],
        },
      ],
      sourceIds: ["1", "2"],
    },
    {
      id: "formuller-neden-farkli",
      title: "Formüller neden farklı sonuç verir?",
      paragraphs: [
        "Epley, Brzycki, Lander, Lombardi ve Mayhew denklemleri farklı veri kümeleri ve matematiksel yaklaşımlarla geliştirilmiştir. Aynı yük ve tekrar sayısında birkaç kilogramlık fark görmen normaldir. Hesaplayıcının formülleri ayrı ayrı göstermesinin nedeni, tek bir denklemi tartışmasız doğru kabul etmemektir.",
        "Denklemler tekrar sayısını performans kaybıyla ilişkilendirir; fakat tekrar dayanıklılığı hareketten harekete ve kişiden kişiye değişir. Squat setinde on tekrar yapabilen bir sporcu, aynı yüzdeyle bench press'te farklı tekrar sayısına ulaşabilir. Bu yüzden bir harekette elde edilen tahmini başka harekete taşımamalısın.",
        "2025 tarihli geniş bir çapraz doğrulama çalışması, çok sayıda denklemi farklı hareket ve tekrar aralıklarında karşılaştırdı; sonuçlar en uygun denklemin harekete ve kullanılan tekrar sayısına göre değişebildiğini gösterdi. Bu bulgu, tek formülü her koşulda üstün ilan etmek yerine aynı yöntemi tutarlı biçimde izlemeyi destekler.",
      ],
      table: {
        caption: "Tahmin kalitesini etkileyen başlıca değişkenler",
        headers: ["Değişken", "Neden önemli?", "Pratik yaklaşım"],
        rows: [
          [
            "Tekrar sayısı",
            "Tekrar yükseldikçe kişisel dayanıklılık farkı büyür.",
            "Mümkünse düşük-orta tekrar aralığındaki kaliteli setleri kullan.",
          ],
          [
            "RPE / RIR",
            "Denklem sette kaç tekrar kaldığını bilmez.",
            "Tükenişten uzak seti kesin maksimum gibi yorumlama.",
          ],
          [
            "Hareket standardı",
            "Tempo, duraklama ve hareket mesafesi sonucu değiştirir.",
            "Aynı varyasyonu aynı standartla karşılaştır.",
          ],
          [
            "Ekipman",
            "Kemer, sargı, bar ve zemin performansı etkileyebilir.",
            "Kayıtlarda ekipman ve koşulu not et.",
          ],
        ],
      },
      sourceIds: ["1", "3", "4"],
    },
    {
      id: "dogru-seti-sec",
      title: "Hesaplama için doğru seti seç",
      paragraphs: [
        "En ağır set her zaman en iyi veri değildir. Teknik bozulmuşsa, hareket mesafesi kısalmışsa veya bir yardımcı tekrara dönüşmüşse sayı antrenmandaki hedef hareketi temsil etmez. Hesaba yalnız tamamlandığından emin olduğun tekrarları gir.",
        "Bir ile on tekrar arasındaki setler pratikte daha sık kullanılır. Araç daha yüksek tekrarları kabul etse de tekrar sayısı arttıkça kas dayanıklılığı, tempo ve motivasyon sonucu daha fazla etkiler. Yirmi tekrarlı bir setten çıkan E1RM, yüzde tabanlı güç programlaması için üç ya da beş tekrarlı güçlü bir set kadar güvenilir olmayabilir.",
      ],
      steps: [
        "Hesaplamak istediğin hareketi ve varyasyonu netleştir: yarışma squat'ı ile tempo squat aynı kayıt değildir.",
        "Tam hareket mesafesinde, teknik standardı bozulmadan bitirdiğin bir set seç.",
        "Bar ağırlığını ve yalnız tamamlanan tekrarları gir.",
        "Sonucu setin RPE/RIR değeri, o günkü yorgunluk ve video kaydıyla birlikte değerlendir.",
        "İlerlemeyi aynı hareket, benzer tekrar aralığı ve benzer koşullarla kıyasla.",
      ],
      note: "Ağrı, sakatlık şüphesi veya tekniği kontrol edemediğin bir yük varsa maksimum test yapma. E1RM de tıbbi veya teknik değerlendirme yerine geçmez.",
    },
    {
      id: "yuzdeyle-programlama",
      title: "E1RM ile antrenman yüzdesi planlamak",
      paragraphs: [
        "Yüzde tabanlı programlarda E1RM, çalışma ağırlığını hesaplayan referanstır. E1RM değerin 150 kg ise yüzde 70 teorik olarak 105 kg'dır. Ancak bu sayı otomatik emir değildir. Günlük hazır oluş, hareket varyasyonu ve hedef tekrar sayısı yükte ayarlama gerektirebilir.",
        "Programda tempo squat, pause bench press veya deficit deadlift gibi varyasyonlar varsa yüzdelerin hangi ana harekete bağlandığını kontrol et. Varyasyonun zorluğu yarışma hareketinden farklıdır. Yüzde ana hareket E1RM'inden gelse bile hedef RPE, set kalitesi ve programın amacı son kararda belirleyicidir.",
        "AtalayTheCoach programlarında ana hareket referansı tanımlı varyasyonlar, kayıtlı E1RM değerinden önerilen ek yükü otomatik hesaplayabilir. Bu kolaylık yanlış bir başlangıç E1RM'ini doğru hâle getirmez. İlk haftalarda önerilen yükün hedef RPE ile uyuşup uyuşmadığını izle ve gerektiğinde değeri güncelle.",
      ],
      subsections: [
        {
          title: "Training max kullanmak",
          paragraphs: [
            "Bazı programlar gerçek veya tahmini maksimum yerine bunun yüzde 90–95'i gibi daha temkinli bir training max kullanır. Bu yaklaşım kötü günlerde teknik kalitenin korunmasına ve uzun bloklarda yükün daha yönetilebilir kalmasına yardımcı olabilir. Program hangi referansı istiyorsa yüzdeleri o değer üzerinden uygula; E1RM ve training max'i karıştırma.",
          ],
        },
      ],
      sourceIds: ["2"],
    },
    {
      id: "gelisimi-takip-et",
      title: "Gelişimi tek bir sayıya indirgeme",
      paragraphs: [
        "E1RM'nin yükselmesi olumlu olabilir, fakat gelişimin tek kanıtı değildir. Aynı yükte daha düşük RPE, daha temiz teknik, daha kısa toparlanma veya daha fazla kontrollü tekrar da ilerlemedir. Buna karşılık kötü teknikle elde edilen yüksek tahmin gerçek güç artışını abartabilir.",
        "Günlük dalgalanmalara tepki vermek yerine birkaç haftalık eğilime bak. Uyku, kalori alımı, stres ve önceki antrenman yükü tek bir günün sonucunu etkileyebilir. Aynı hareketin benzer tekrar aralığındaki en iyi kaliteli setlerini karşılaştırmak daha anlamlıdır.",
        "E1RM sürekli düşüyorsa hemen programı değiştirmeden önce veri kalitesini kontrol et: hareket standardı aynı mı, set gerçekten hedef RPE'de mi, ekipman veya ölçüm değişti mi? Ardından toparlanma ve hacim dengesini değerlendir. Bir sayı karar sürecini desteklemeli; tek başına yönetmemelidir.",
      ],
      sourceIds: ["2", "4"],
    },
    {
      id: "yaygin-hatalar",
      title: "Sık yapılan hatalar",
      paragraphs: [
        "Hesaplama basit olduğu için en büyük hatalar formülde değil, girdide ve yorumdadır. Aşağıdaki durumlar tahmini olduğundan daha yüksek ya da düşük gösterebilir.",
      ],
      bullets: [
        "Yardımla tamamlanan veya hareket mesafesi eksik tekrarları saymak.",
        "On beş-yirmi tekrarlı tükeniş setini düşük tekrar gücünün kesin göstergesi kabul etmek.",
        "Pause bench sonucunu touch-and-go bench kaydıyla doğrudan karşılaştırmak.",
        "Sette tekrar rezervi varken çıkan değeri gerçek maksimum sanmak.",
        "Her antrenmandaki küçük düşüşte profil E1RM'ini değiştirmek.",
        "Squat için hesaplanan ilişkiyi bench press veya deadlift'e uygulamak.",
        "Yüzde önerisini hedef RPE ve teknik kaliteden bağımsız uygulamak.",
      ],
    },
    {
      id: "uygulama-ornegi",
      title: "Uygulama örneği: aynı setten daha iyi karar çıkarmak",
      paragraphs: [
        "Bench press'te 100 kg ile beş tekrar yaptığını düşün. Hesaplayıcı formüllere göre birbirine yakın birkaç E1RM ve bunların ortalamasını gösterecek. Yalnız ortalama sayıyı profile kaydetmeden önce seti izle: son tekrarın hızı belirgin düştü mü, göğüste bekleme standardı korundu mu, sette kaç tekrar daha kalmıştı? RPE 10 ve yarışma standardında bir set ile RPE 8 touch-and-go set aynı anlama gelmez.",
        "Bir sonraki hafta 102,5 kg ile yine beş tekrar yapıp benzer teknik ve RPE elde edersen ilerleme yorumu daha güçlüdür. Buna karşılık 105 kg ile hareket mesafesi kısalmış dört tekrar, formülde yüksek görünse de program referansını güncellemek için daha zayıf veridir. Video, RPE ve hareket standardı sayıyı doğrulayan bağlamdır.",
        "Program yüzde 75 ile dört set altı tekrar istiyorsa önerilen ağırlığı en yakın uygulanabilir plakaya yuvarla ve ilk çalışma setinde hedef RPE'yi kontrol et. Beklenenden ağırsa E1RM'ini kanıtlamak için seti zorlamak yerine yükü ayarla. Yüzde, antrenman kararını başlatır; set kalitesi kararı tamamlar.",
      ],
    },
    {
      id: "harekete-ozgu-takip",
      title: "Her hareket için ayrı referans tut",
      paragraphs: [
        "Squat, bench press ve deadlift aynı tekrar-yüzde ilişkisine sahip değildir. Hatta aynı hareketin low-bar, high-bar, pause veya tempo varyasyonlarında bile teknik ve tekrar dayanıklılığı değişir. Kayıt adında varyasyonu açıkça belirtmek, sonraki ay yanlış setleri karşılaştırmanı önler.",
        "Bir ana harekete bağlı varyasyon program yüzdesini ana hareket E1RM'inden alabilir; bu, varyasyonun kendi tahmini maksimumuyla aynı şey değildir. Amaç program yükünü ortak referanstan yönetmektir. Varyasyon beklenen RPE'den sapıyorsa önce teknik ve varyasyon zorluğunu değerlendir, ana hareket E1RM'ini tek set yüzünden değiştirme.",
        "Uzun vadeli takipte tarih, yük, tekrar, RPE, ekipman ve hareket standardını aynı kayıtta sakla. Böylece yalnız tahmini sayı değil, o sayının hangi koşulda üretildiği de görünür kalır. Bu kayıt disiplini küçük formül farklarından daha çok karar kalitesini artırır.",
      ],
    },
  ],
  faqs: [
    {
      question: "1RM ve E1RM aynı mı?",
      answer:
        "Hayır. 1RM doğrudan tamamlanmış maksimum tek tekrardır; E1RM daha hafif bir setten formülle üretilen tahmindir.",
    },
    {
      question: "Kaç tekrara kadar tahmin kullanmalıyım?",
      answer:
        "Araç 1–20 tekrar kabul eder. Kuvvet programlamasında düşük ve orta tekrar aralığındaki teknik olarak temiz setler genellikle daha kullanışlıdır; tekrar yükseldikçe kişisel dayanıklılık farkı büyür.",
    },
    {
      question: "Hangi formül en doğru?",
      answer:
        "Her hareket ve sporcu için tek bir üstün formül yoktur. Ortalama sonucu kullanabilir veya aynı hareketi sürekli aynı formülle takip edebilirsin. Tutarlılık, küçük formül farklarından daha değerlidir.",
    },
    {
      question: "RPE 8 setten çıkan sonucu kullanabilir miyim?",
      answer:
        "Evet, ilerleme takibinde kullanabilirsin; ancak sette yaklaşık iki tekrar kaldığı için sonucu gerçek maksimum gibi kabul etmemelisin. Aynı RPE ve tekrar aralığındaki setlerle karşılaştır.",
    },
    {
      question: "Her egzersiz için ayrı E1RM gerekir mi?",
      answer:
        "Evet. Hareket standardı ve tekrar dayanıklılığı değişir. Ana hareket referansına bağlanmış varyasyon yüzdeleri programda otomatik hesaplanabilir, fakat varyasyon performansı yine kendi bağlamında yorumlanmalıdır.",
    },
    {
      question: "E1RM ne sıklıkla güncellenmeli?",
      answer:
        "Benzer koşullarda belirgin biçimde daha güçlü ve teknik olarak geçerli bir set yaptığında güncellemek yeterlidir. Her günlük dalgalanmada değiştirmek zorunda değilsin.",
    },
    {
      question: "Hesaplanan ağırlığı doğrudan denemeli miyim?",
      answer:
        "Hayır. Sonuç bir tahmindir. Maksimum deneme için uygun hazırlık, güvenlik ekipmanı, spotter ve teknik yeterlilik gerekir.",
    },
    {
      question: "Kilo verirken E1RM düşerse güç kaybettim mi?",
      answer:
        "Tek bir düşüş bunu kanıtlamaz. Enerji durumu, yorgunluk, teknik ve set seçimi sonucu etkiler. Birkaç haftalık eğilim ile vücut ağırlığına göre performansı birlikte değerlendir.",
    },
    {
      question: "Plakaya yuvarlama tahmini bozar mı?",
      answer:
        "Hesaplanan çalışma ağırlığını salondaki uygulanabilir plakaya yuvarlamak normaldir. Küçük farkı hedef RPE ve teknik kaliteyle yönet; yüzde sonucunu zorunlu ve kusursuz bir yük gibi uygulama.",
    },
  ],
  sources: [
    {
      id: "1",
      label:
        "Wood ve arkadaşları (2025) — 18 farklı 1RM tahmin denkleminin çapraz doğrulaması",
      href: "https://pubmed.ncbi.nlm.nih.gov/39495260/",
    },
    {
      id: "2",
      label: "ACSM — direnç antrenmanında ilerleme modelleri",
      href: "https://pubmed.ncbi.nlm.nih.gov/19204579/",
    },
    {
      id: "3",
      label: "LeSuer ve arkadaşları — çok tekrarlı setlerden 1RM tahmini",
      href: "https://pubmed.ncbi.nlm.nih.gov/12741856/",
    },
    {
      id: "4",
      label:
        "Hackett ve arkadaşları — tekrar sayısı ve %1RM ilişkisi üzerine meta-regresyon",
      href: "https://pubmed.ncbi.nlm.nih.gov/37792272/",
    },
  ],
  related: [
    {
      href: "/essay/ipf-gl-calculator",
      label: "IPF GL puanını hesapla",
      description: "Powerlifting totalini vücut ağırlığına göre karşılaştır.",
    },
    {
      href: "/essay/wilks-calculator",
      label: "Wilks skorunu hesapla",
      description: "Aynı totali klasik Wilks katsayısıyla gör.",
    },
  ],
  primaryCta: {
    href: "/program",
    label: "Hazır programları incele",
    description: "Yüzde ve RPE tabanlı programlarla güç gelişimini planla.",
  },
};
